import React, { createContext, useContext, useState, useEffect } from 'react';
import { getApiUrl } from '../utils/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Validate token with backend using /me endpoint
  const validateToken = async (token) => {
    if (!token) return false;
    
    try {
      // Use AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout
      
      const response = await fetch(`${getApiUrl()}/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      // If we get a 401, token is invalid
      if (response.status === 401) {
        return false;
      }
      
      // If successful, token is valid
      if (response.ok) {
        return true;
      }
      
      // For other errors, assume valid (network issues, etc.)
      return true;
    } catch (error) {
      // Network error, timeout, or abort - don't invalidate session
      // Assume valid for backward compatibility and better UX
      if (error.name === 'AbortError') {
        console.warn('Token validation timeout - assuming valid');
      } else {
        console.warn('Token validation failed (network error):', error.message);
      }
      return true; // Assume valid to prevent unnecessary logouts
    }
  };

  useEffect(() => {
    // Check if user is logged in from localStorage
    const initializeAuth = async () => {
      try {
        const savedUser = localStorage.getItem('user');
        if (savedUser) {
          try {
            const userData = JSON.parse(savedUser);
            
            // Validate token if it exists
            if (userData.token) {
              // Restore user immediately for better UX
              setUser(userData);
              
              // Validate token in background (non-blocking)
              validateToken(userData.token).then(isValid => {
                if (!isValid) {
                  // Token invalid - clear storage
                  console.warn('Token validation failed, clearing session');
                  localStorage.removeItem('user');
                  setUser(null);
                }
                // If valid, user is already set above
              }).catch(error => {
                // Error during validation - keep user logged in
                console.warn('Token validation error (keeping session):', error.message);
              });
            } else {
              // No token but user data exists - restore it anyway (backward compatibility)
              setUser(userData);
            }
          } catch (error) {
            console.error('Error parsing saved user data:', error);
            localStorage.removeItem('user');
            setUser(null);
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        setUser(null);
      } finally {
        // Always set loading to false, even if there's an error
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = (userData) => {
    // Ensure company is stored with user data
    const userWithCompany = {
      ...userData,
      company: userData.company || 'menthing',
      loginTime: Date.now() // Track login time for session management
    };
    setUser(userWithCompany);
    localStorage.setItem('user', JSON.stringify(userWithCompany));
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    // Also clear any other persisted state
    localStorage.removeItem('appState');
  };

  const updateUser = (updates) => {
    if (user) {
      const updatedUser = { ...user, ...updates };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const isAdmin = () => {
    return user && user.role === 'admin';
  };

  const isEmployee = () => {
    return user && user.role === 'employee';
  };

  const value = {
    user,
    login,
    logout,
    updateUser,
    isAdmin,
    isEmployee,
    isLoading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
